import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiFetch, resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo, initials } from "@/lib/format";
import { PageLoader, EmptyState, ErrorState } from "@/components/common";
import { MessageCircle, ShieldAlert } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const { data: convs, isLoading, isError, refetch } = useQuery({
    queryKey: ["conversations"],
    queryFn: () => apiFetch("/conversations"),
    refetchInterval: 15000,
  });

  if (isLoading) return <PageLoader />;
  if (isError) return <ErrorState onRetry={refetch} />;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <h1 className="font-display text-2xl font-extrabold">Messages</h1>
      <div className="mt-6 space-y-2">
        {convs.length === 0 ? (
          <EmptyState icon={MessageCircle} title="No conversations yet" message="Start chatting with a seller from any listing." />
        ) : (
          convs.map((c) => {
            const other = c.other_user;
            const isAdminThread = c.admin_thread;
            const suspended = other?.banned;
            return (
              <Link key={c.id} to={`/chat/${c.id}`} data-testid={`conversation-${c.id}`} className="flex items-center gap-3 rounded-2xl border border-border p-3 transition hover:bg-secondary">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand text-sm font-bold text-white">
                  {c.listing?.images?.[0] ? <img src={resolveImage(c.listing.images[0])} alt="" className="h-full w-full object-cover" /> : (isAdminThread ? "VT" : initials(other?.name))}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold">{isAdminThread ? (c.subject || "Venda Team") : (other?.name || "User")}</span>
                    {c.appeal && <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700"><ShieldAlert className="h-3 w-3" />APPEAL</span>}
                    {suspended && !isAdminThread && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-600">SUSPENDED</span>}
                  </div>
                  {c.listing && <p className="truncate text-xs text-muted-foreground">{c.listing.title}</p>}
                  <p className="truncate text-sm text-muted-foreground">{c.last_message || "No messages yet"}</p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(c.last_at)}</span>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
